/**
 * Excel or CSV import with a mandatory preview -- the same rule as the
 * chart import: the server runs the real import, reports what it would
 * do, and rolls back. Only then is the Import button offered.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack,
  Typography,
} from '@mui/material'
import UploadIcon from '@mui/icons-material/UploadFileOutlined'
import { useTranslation } from 'react-i18next'

import { errorMessage } from '../api/client'
import type { InvImportResult } from '../api/types'
import { c, fmtMoney } from '../theme'

export default function ImportDialog({ title, help, sample, run, invalidate, onClose }: {
  title: string
  help: string
  sample: string
  run: (file: File, dryRun: boolean) => Promise<InvImportResult>
  /** Query keys to refresh after a real import. */
  invalidate: string[]
  onClose: () => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<InvImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function go(dryRun: boolean) {
    if (!file) return
    setBusy(true); setError(null)
    try {
      const r = await run(file, dryRun)
      if (dryRun) { setPreview(r); return }
      for (const k of invalidate) void qc.invalidateQueries({ queryKey: [k] })
      onClose()
    } catch (e) { setError(errorMessage(e)) } finally { setBusy(false) }
  }

  const willDo = preview && ((preview.created ?? 0) + (preview.updated ?? 0) > 0 || preview.ok)

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" sx={{ color: c.inkSecondary, mb: 1.5 }}>{help}</Typography>
        <Box component="pre" sx={{ fontFamily: 'var(--mono, monospace)', fontSize: 11.5,
          bgcolor: c.surfacePage, border: `1px solid ${c.border}`, borderRadius: 1,
          p: 1.5, m: 0, overflowX: 'auto' }}>{sample}</Box>

        <Button component="label" variant="outlined" startIcon={<UploadIcon />} sx={{ mt: 2 }}>
          {file ? file.name : t('inv.chooseFile')}
          <input type="file" hidden
            accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPreview(null) }} />
        </Button>

        {preview && (
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Alert severity={preview.rejected.length ? 'warning' : 'success'}>
              {preview.created !== undefined
                ? t('inv.previewItems', { rows: preview.rows, created: preview.created,
                                          updated: preview.updated ?? 0,
                                          rejected: preview.rejected.length })
                : preview.ok
                  ? t('inv.previewOpening', { lines: preview.lines ?? 0,
                                              value: fmtMoney(preview.store_value ?? '0') })
                  : t('inv.previewOpeningBad', { rejected: preview.rejected.length })}
            </Alert>
            {preview.rejected.length > 0 && (
              <Box sx={{ maxHeight: 220, overflowY: 'auto', border: `1px solid ${c.border}`,
                         borderRadius: 1 }}>
                <table className="rpt">
                  <thead><tr><th style={{ width: 60 }}>{t('inv.row')}</th>
                    <th style={{ width: 110 }}>{t('inv.code')}</th><th>{t('inv.reason')}</th></tr></thead>
                  <tbody>
                    {preview.rejected.map((r, i) => (
                      <tr key={i}><td className="num" style={{ textAlign: 'start' }}>{r.row ?? '—'}</td>
                        <td>{r.code ?? ''}</td><td>{r.reason}</td></tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        {!preview ? (
          <Button variant="contained" disabled={!file || busy} onClick={() => go(true)}>
            {t('inv.preview')}</Button>
        ) : (
          <Button variant="contained" disabled={!willDo || busy} onClick={() => go(false)}>
            {t('inv.importNow')}</Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
